import { ExclusivityEntry, ExclusivityMap } from 'types';
import { consumer, core, safeEnumCompare } from '@tock/coretypes';

import { Instant, LocalDate, LocalDateTime } from 'js-joda';
import { defaultMemoize } from 'reselect';
import { oc } from 'ts-optchain';
import _ from 'lodash';

import { getPurchaseSizeFromPartySize } from '@tock/shared/utils/offerings';
import protos from 'utils/protos';

const { Exclusivity } = protos;

interface DateTimeStringSpec {
  timestampSecs: number;
  date: string;
  dateTime: string;
}

// The way that this is written, this will be computed only once and then maintain
// the same value until page reload, even as Instant.now() changes.
const getNow: () => DateTimeStringSpec = defaultMemoize(() => {
  const now = Instant.now();
  const localNow = LocalDateTime.ofInstant(now);
  return {
    timestampSecs: now.epochSecond(),
    date: localNow.toLocalDate().toString(),
    dateTime: localNow.toString(),
  };
});

function isBookable(ticketGroup: consumer.ICalendarTicketGroup): boolean {
  const now = getNow();
  if (!ticketGroup.date) {
    return false;
  }
  // If the server has provided us with an absolute start instant, use that. If
  // not, fall back on the slower and buggy method of comparing local date time
  // strings.
  // TODO(jacob): Once the server change that adds these timestamps has been in
  // production long enough, remove this fallback.
  if (ticketGroup.cutoffTimeSecs) {
    return now.timestampSecs <= ticketGroup.cutoffTimeSecs;
  } else if (ticketGroup.startTimeSecs) {
    return now.timestampSecs <= ticketGroup.startTimeSecs;
  } else {
    return now.date < ticketGroup.date || now.dateTime < `${ticketGroup.date}T${ticketGroup.time}`;
  }
}

export function ticketGroupMatchesTicketTypeAndPurchaseSize(
  ticketGroup: consumer.ICalendarTicketGroup,
  ticketTypeId: number,
  purchaseSize: number
): boolean {
  return (
    isBookable(ticketGroup) &&
    ticketGroupMatchesTicketTypeId(ticketGroup, ticketTypeId) &&
    ticketGroupMatchesPurchaseSize(ticketGroup, purchaseSize)
  );
}

export function ticketGroupMatchesTicketTypeId(
  ticketGroup: consumer.ICalendarTicketGroup,
  ticketTypeId: number
) {
  return _.some(ticketGroup.ticketTypePrice, (price) => price.ticketTypeId === ticketTypeId);
}

export function ticketGroupMatchesPartySize(
  ticketGroup: consumer.ICalendarTicketGroup,
  partySize: number
) {
  return ticketGroupMatchesPurchaseSize(
    ticketGroup,
    getPurchaseSizeFromPartySize(partySize, ticketGroup.singleUnitQuantity ?? 1)
  );
}

export function ticketGroupMatchesPurchaseSize(
  ticketGroup: consumer.ICalendarTicketGroup,
  purchaseSize: number
) {
  if (ticketGroup.isEvensOnly && purchaseSize % 2 !== 0) {
    return false;
  }
  return (
    isBookable(ticketGroup) &&
    purchaseSize >= (ticketGroup.minPurchaseSize || 0) &&
    purchaseSize <= (ticketGroup.maxPurchaseSize || 0)
  );
}

// Note that the number associated with each of these statuses is important: We merge two statuses (e.g. status
// on two different ticket groups in the same kind) by taking the minimum-valued status. (So, e.g., if one
// ticket group is sold out and another is locked, the merged status is locked instead of sold out since locked
// is a lower-numbered status.)
export enum TicketGroupStatus {
  AVAILABLE = 0, // Ticket group can be purchased with the given type and size
  LOCKED = 1, // Ticket group offers the type and size requested and is not sold out, but too many
  // tickets are locked to purchase the requested number of tickets
  SOLD_OUT = 2, // Ticket group offers the type and size requested, but it is already sold out of it
  STRAGGLER_PROTECTED = 3, // Ticket group normally offers the type and size available and it is not sold out,
  // but it is not offering the given size due to communal table straggler protection
  NOT_OFFERED = 4, // Ticket group does not offer the given type and size at all
}

export function ticketGroupStatusForPurchaseSize(
  ticketGroup: consumer.ICalendarTicketGroup,
  purchaseSize: number
) {
  if (!ticketGroupMatchesPurchaseSize(ticketGroup, purchaseSize)) {
    return TicketGroupStatus.NOT_OFFERED;
  }

  if (ticketGroup.isCommunal) {
    if ((ticketGroup.availableTickets || 0) < purchaseSize) {
      if ((ticketGroup.availableTickets || 0) + (ticketGroup.lockedTickets || 0) >= purchaseSize) {
        return TicketGroupStatus.LOCKED;
      } else {
        return TicketGroupStatus.SOLD_OUT;
      }
    }
    if (!ticketGroup.allowsStragglers && ticketGroup.availableTickets === purchaseSize + 1) {
      return TicketGroupStatus.STRAGGLER_PROTECTED;
    }
    if (ticketGroup.isEvensOnly && purchaseSize % 2 !== 0) {
      return TicketGroupStatus.NOT_OFFERED;
    }
    return TicketGroupStatus.AVAILABLE;
  } else {
    if (ticketGroup.soldTickets !== 0) {
      return TicketGroupStatus.SOLD_OUT;
    }
    if (ticketGroup.lockedTickets !== 0) {
      return TicketGroupStatus.LOCKED;
    }
    if (ticketGroup.isEvensOnly && purchaseSize % 2 !== 0) {
      return TicketGroupStatus.NOT_OFFERED;
    }
    return TicketGroupStatus.AVAILABLE;
  }
}

export function ticketGroupStatusForPartySize(
  ticketGroup: consumer.ICalendarTicketGroup,
  partySize: number
) {
  return ticketGroupStatusForPurchaseSize(
    ticketGroup,
    getPurchaseSizeFromPartySize(partySize, ticketGroup.singleUnitQuantity ?? 1)
  );
}

function ticketGroupStatusForTicketTypeAndPurchaseSize(
  ticketGroup: consumer.ICalendarTicketGroup,
  ticketTypeId: number,
  purchaseSize: number
) {
  if (!ticketGroupMatchesTicketTypeAndPurchaseSize(ticketGroup, ticketTypeId, purchaseSize)) {
    return TicketGroupStatus.NOT_OFFERED;
  }
  return ticketGroupStatusForPurchaseSize(ticketGroup, purchaseSize);
}

export function ticketTypeStatusForPurchaseSize(
  ticketGroups: consumer.CalendarTicketGroup[],
  ticketTypeId: number,
  purchaseSize: number
) {
  const statuses = _.map(ticketGroups, (ticketGroup) =>
    ticketGroupStatusForTicketTypeAndPurchaseSize(ticketGroup, ticketTypeId, purchaseSize)
  );
  return _.min(statuses);
}

function ticketGroupAvailableForPartySize(
  ticketGroup: consumer.ICalendarTicketGroup,
  partySize: number
) {
  return ticketGroupStatusForPartySize(ticketGroup, partySize) === TicketGroupStatus.AVAILABLE;
}

function ticketGroupAvailableForPurchaseSize(
  ticketGroup: consumer.CalendarTicketGroup,
  purchaseSize: number
) {
  return (
    ticketGroupStatusForPurchaseSize(ticketGroup, purchaseSize) === TicketGroupStatus.AVAILABLE
  );
}

function ticketGroupAvailableForTicketTypeAndPurchaseSize(
  ticketGroup: consumer.ICalendarTicketGroup,
  ticketTypeId: number,
  purchaseSize: number
) {
  return (
    ticketGroupStatusForTicketTypeAndPurchaseSize(ticketGroup, ticketTypeId, purchaseSize) ===
    TicketGroupStatus.AVAILABLE
  );
}

function ticketGroupHasAvailableCommunalSeats(
  ticketGroup: consumer.CalendarTicketGroup,
  ticketTypeId: number
) {
  return (
    isBookable(ticketGroup) &&
    ticketGroupMatchesTicketTypeId(ticketGroup, ticketTypeId) &&
    ticketGroup.isCommunal &&
    ticketGroup.availableTickets > 0
  );
}

function ticketGroupFullyLockedForPurchaseSize(
  ticketGroup: consumer.CalendarTicketGroup,
  purchaseSize: number
) {
  return ticketGroupStatusForPurchaseSize(ticketGroup, purchaseSize) === TicketGroupStatus.LOCKED;
}

function ticketGroupFullyLockedForPartySize(
  ticketGroup: consumer.CalendarTicketGroup,
  partySize: number
) {
  return ticketGroupStatusForPartySize(ticketGroup, partySize) === TicketGroupStatus.LOCKED;
}

function ticketGroupFullyLockedForTicketTypeAndPurchaseSize(
  ticketGroup: consumer.CalendarTicketGroup,
  ticketTypeId: number,
  purchaseSize: number
) {
  return (
    ticketGroupStatusForTicketTypeAndPurchaseSize(ticketGroup, ticketTypeId, purchaseSize) ===
    TicketGroupStatus.LOCKED
  );
}

function ticketGroupOfferedForPartySize(
  ticketGroup: consumer.ICalendarTicketGroup,
  partySize: number
) {
  return (
    ticketGroupMatchesPartySize(ticketGroup, partySize) &&
    (ticketGroup.numTickets || 0) >= partySize
  );
}

function ticketGroupOfferedForPurchaseSize(
  ticketGroup: consumer.CalendarTicketGroup,
  purchaseSize: number
) {
  return (
    ticketGroupMatchesPurchaseSize(ticketGroup, purchaseSize) &&
    ticketGroup.numTickets >= purchaseSize
  );
}

function ticketGroupOfferedForTicketTypeAndPurchaseSize(
  ticketGroup: consumer.CalendarTicketGroup,
  ticketTypeId: number,
  purchaseSize: number
) {
  return (
    ticketGroupMatchesTicketTypeAndPurchaseSize(ticketGroup, ticketTypeId, purchaseSize) &&
    ticketGroup.numTickets >= purchaseSize
  );
}

export const range = defaultMemoize((valueMap?: Dictionary<boolean>) => {
  const array = _.keys(valueMap);
  return {
    min: _.min(array),
    max: _.max(array),
  };
});

function accumulateByDate(acc: DateMap, ticketGroup: consumer.ICalendarTicketGroup): DateMap {
  acc[ticketGroup.businessDay || ''] = true;
  return acc;
}

export const partySizeAvailableDates = defaultMemoize(
  (ticketGroups: consumer.ICalendarTicketGroup[], partySize: number): DateMap => {
    return _.reduce(
      _.filter(ticketGroups, (ticketGroup) => {
        return ticketGroupAvailableForPartySize(ticketGroup, partySize);
      }),
      accumulateByDate,
      {}
    );
  }
);

// There are at present no callers for this.
export const purchaseSizeAvailableDates = defaultMemoize(
  (ticketGroups: consumer.CalendarTicketGroup[], purchaseSize: number): DateMap => {
    return _.reduce(
      _.filter(ticketGroups, (ticketGroup) => {
        return ticketGroupAvailableForPurchaseSize(ticketGroup, purchaseSize);
      }),
      accumulateByDate,
      {}
    );
  }
);

export const partySizeLockedDates = defaultMemoize(
  (ticketGroups: consumer.CalendarTicketGroup[], partySize: number): DateMap => {
    return _.reduce(
      _.filter(ticketGroups, (ticketGroup) => {
        return (
          ticketGroupOfferedForPartySize(ticketGroup, partySize) &&
          ticketGroupFullyLockedForPartySize(ticketGroup, partySize)
        );
      }),
      accumulateByDate,
      {}
    );
  }
);

export const purchaseSizeLockedDates = defaultMemoize(
  (ticketGroups: consumer.CalendarTicketGroup[], purchaseSize: number): DateMap => {
    return _.reduce(
      _.filter(ticketGroups, (ticketGroup) => {
        return (
          ticketGroupOfferedForPurchaseSize(ticketGroup, purchaseSize) &&
          ticketGroupFullyLockedForPurchaseSize(ticketGroup, purchaseSize)
        );
      }),
      accumulateByDate,
      {}
    );
  }
);

export const partySizeSoldDates = defaultMemoize(
  (ticketGroups: consumer.ICalendarTicketGroup[], partySize: number): DateMap => {
    return _.reduce(
      _.filter(ticketGroups, (ticketGroup) => {
        return (
          ticketGroupOfferedForPartySize(ticketGroup, partySize) &&
          !ticketGroupAvailableForPartySize(ticketGroup, partySize)
        );
      }),
      accumulateByDate,
      {}
    );
  }
);

export const purchaseSizeSoldDates = defaultMemoize(
  (ticketGroups: consumer.CalendarTicketGroup[], purchaseSize: number): DateMap => {
    return _.reduce(
      _.filter(ticketGroups, (ticketGroup) => {
        return (
          ticketGroupOfferedForPurchaseSize(ticketGroup, purchaseSize) &&
          !ticketGroupAvailableForPurchaseSize(ticketGroup, purchaseSize)
        );
      }),
      accumulateByDate,
      {}
    );
  }
);

export const partySizeOfferedDates = defaultMemoize(
  (ticketGroups: consumer.ICalendarTicketGroup[], partySize: number): DateMap => {
    return _.reduce(
      _.filter(ticketGroups, (ticketGroup) => {
        return ticketGroupMatchesPartySize(ticketGroup, partySize);
      }),
      accumulateByDate,
      {}
    );
  }
);

export const purchaseSizeOfferedDates = defaultMemoize(
  (ticketGroups: consumer.CalendarTicketGroup[], purchaseSize: number): DateMap => {
    return _.reduce(
      _.filter(ticketGroups, (ticketGroup) => {
        return ticketGroupMatchesPurchaseSize(ticketGroup, purchaseSize);
      }),
      accumulateByDate,
      {}
    );
  }
);

function getSingleUnitQuantityForTicketTypeFromGroups(
  ticketGroups: consumer.CalendarTicketGroup[],
  ticketTypeId: number
): number {
  const ticketTypeGroup = ticketGroups.find((tg) =>
    ticketGroupMatchesTicketTypeId(tg, ticketTypeId)
  );
  return ticketTypeGroup?.singleUnitQuantity ?? 1;
}

export const ticketTypePartySizeAvailableDates = defaultMemoize(
  (
    ticketGroups: consumer.CalendarTicketGroup[],
    ticketTypeId: number,
    partySize: number
  ): DateMap => {
    const singleUnitQuantity = getSingleUnitQuantityForTicketTypeFromGroups(
      ticketGroups,
      ticketTypeId
    );
    return ticketTypePurchaseSizeAvailableDates(
      ticketGroups,
      ticketTypeId,
      getPurchaseSizeFromPartySize(partySize, singleUnitQuantity)
    );
  }
);

export const ticketTypePurchaseSizeAvailableDates = defaultMemoize(
  (
    ticketGroups: consumer.ICalendarTicketGroup[],
    ticketTypeId: number,
    purchaseSize: number
  ): DateMap => {
    return _.reduce(
      _.filter(ticketGroups, (ticketGroup) => {
        return ticketGroupAvailableForTicketTypeAndPurchaseSize(
          ticketGroup,
          ticketTypeId,
          purchaseSize
        );
      }),
      accumulateByDate,
      {}
    );
  }
);

export const ticketTypePurchaseSizeLockedDates = defaultMemoize(
  (
    ticketGroups: consumer.CalendarTicketGroup[],
    ticketTypeId: number,
    purchaseSize: number
  ): DateMap => {
    return _.reduce(
      _.filter(ticketGroups, (ticketGroup) => {
        return (
          ticketGroupOfferedForTicketTypeAndPurchaseSize(ticketGroup, ticketTypeId, purchaseSize) &&
          ticketGroupFullyLockedForTicketTypeAndPurchaseSize(
            ticketGroup,
            ticketTypeId,
            purchaseSize
          )
        );
      }),
      accumulateByDate,
      {}
    );
  }
);

export const ticketTypePurchaseSizeSoldDates = defaultMemoize(
  (
    ticketGroups: consumer.CalendarTicketGroup[],
    ticketTypeId: number,
    purchaseSize: number
  ): DateMap => {
    return _.reduce(
      _.filter(ticketGroups, (ticketGroup) => {
        return (
          ticketGroupOfferedForTicketTypeAndPurchaseSize(ticketGroup, ticketTypeId, purchaseSize) &&
          !ticketGroupAvailableForTicketTypeAndPurchaseSize(ticketGroup, ticketTypeId, purchaseSize)
        );
      }),
      accumulateByDate,
      {}
    );
  }
);

export const getTicketTypeOfferedDates = defaultMemoize(
  (ticketGroups: consumer.ICalendarTicketGroup[], ticketTypeId: number): DateMap => {
    return _.reduce(
      _.filter(ticketGroups, (ticketGroup) => {
        return ticketGroupMatchesTicketTypeId(ticketGroup, ticketTypeId);
      }),
      accumulateByDate,
      {}
    );
  }
);

export const ticketTypePurchaseSizeOfferedDates = defaultMemoize(
  (
    ticketGroups: consumer.ICalendarTicketGroup[],
    ticketTypeId: number,
    purchaseSize: number
  ): DateMap => {
    return _.reduce(
      _.filter(ticketGroups, (ticketGroup) => {
        return ticketGroupMatchesTicketTypeAndPurchaseSize(ticketGroup, ticketTypeId, purchaseSize);
      }),
      accumulateByDate,
      {}
    );
  }
);

export interface Stats {
  priceRange: { min: number; max: number };
  minRequiredSpend?: {
    totalCents?: number;
    priceDisplayDistribution?: core.TicketPriceInformation.PriceDistribution;
  };
  priceDisplayStyle?: consumer.OfferedPriceSummary.PriceDisplayStyle;
  seatingAreaIds?: number[];
  ticketPriceInformation?: core.ITicketPriceInformation;
}

function accumulateByTime(
  purchaseSize: number,
  acc: Dictionary<Stats>,
  ticketGroup: consumer.ICalendarTicketGroup
) {
  const existing = acc[ticketGroup.time || ''];
  const { minRequiredSpend, priceCents, priceDisplayStyle } =
    ticketGroup.ticketTypePrice?.[0] || {};
  const seatingAreaIds = existing?.seatingAreaIds || [];
  if (ticketGroup.seatingAreaId && !existing?.seatingAreaIds?.includes(ticketGroup.seatingAreaId)) {
    seatingAreaIds.push(ticketGroup.seatingAreaId);
  }

  const effectiveStartingAtDisplayPriceCents =
    minRequiredSpend?.totalCentsByPartySize?.[purchaseSize];

  // todo: to display party range TPI pricing, update priceCents to pull from TPI instead
  acc[ticketGroup.time || ''] = {
    priceRange: {
      min: _.min([existing?.priceRange?.min ?? priceCents, priceCents]) as number,
      max: _.max([existing?.priceRange?.max ?? priceCents, priceCents]) as number,
    },
    minRequiredSpend: {
      // Use the max here to ensure that the highest-priced ticket will be displayed in the booking flow.
      // This display-only matches existing business logic in BookingCard.handleBooking
      totalCents: _.max([
        existing?.minRequiredSpend?.totalCents ?? effectiveStartingAtDisplayPriceCents,
        effectiveStartingAtDisplayPriceCents,
      ]) as number,
      priceDisplayDistribution: minRequiredSpend?.distribution ?? undefined,
    },
    priceDisplayStyle: priceDisplayStyle ?? undefined,
    seatingAreaIds: seatingAreaIds,
    ticketPriceInformation: getTicketPriceInformationFromMostExpensiveTicketGroupAtTime(
      ticketGroup,
      existing
    ),
  };

  return acc;
}

// exported for testing
export const getTicketPriceInformationFromMostExpensiveTicketGroupAtTime = (
  ticketGroup: consumer.ICalendarTicketGroup,
  existing: Stats
) => {
  const existingAccumulatedTPI = existing?.ticketPriceInformation;
  const existingAccumulatedPriceCents = existingAccumulatedTPI?.amountCents ?? 0;

  const currentTicketGroupTPI = ticketGroup?.ticketTypePrice?.[0]?.ticketPriceInformation;
  const currentTicketGroupPriceCents = currentTicketGroupTPI?.amountCents ?? 0;

  if (currentTicketGroupPriceCents > existingAccumulatedPriceCents) {
    return currentTicketGroupTPI ?? undefined;
  }

  return existingAccumulatedTPI ?? undefined;
};

function accumulateAvailableTicketsCountByTime(
  acc: Dictionary<number>,
  ticketGroup: consumer.CalendarTicketGroup
) {
  const existing: number | undefined = acc[ticketGroup.time];
  acc[ticketGroup.time] = (existing ?? 0) + ticketGroup.availableTickets;
  return acc;
}

export const ticketTypePurchaseSizeDateAvailableTimes = defaultMemoize(
  (
    ticketGroups: consumer.ICalendarTicketGroup[],
    ticketTypeId: number,
    purchaseSize: number,
    date: string
  ) => {
    return _.reduce(
      _.filter(ticketGroups, (ticketGroup) => {
        return (
          ticketGroupAvailableForTicketTypeAndPurchaseSize(
            ticketGroup,
            ticketTypeId,
            purchaseSize
          ) && ticketGroup.businessDay === date
        );
      }),
      accumulateByTime.bind(null, purchaseSize),
      {}
    );
  }
);

export const seatingAreaAvailableTimes = defaultMemoize(
  (ticketGroups: consumer.CalendarTicketGroup[], seatingAreaId: number | undefined) => {
    return seatingAreaId
      ? ticketGroups.filter((ticketGroup) => ticketGroup.seatingAreaId === seatingAreaId)
      : ticketGroups;
  }
);

/**
 * Computes the total number of available tickets in `ticketGroups` that are
 * communal seating, of the appropriate `ticketTypeId`, that match the given `date`.
 */
export const ticketTypeDateCommunalAvailableTicketCountTimes = defaultMemoize(
  (
    ticketGroups: consumer.CalendarTicketGroup[],
    ticketTypeId: number,
    date: string,
    selectedPartySize: number
  ) => {
    const filteredTicketGroups = ticketGroups.filter(
      (ticketGroup) =>
        ticketGroupHasAvailableCommunalSeats(ticketGroup, ticketTypeId) &&
        ticketGroup.businessDay === date &&
        selectedPartySize >= (ticketGroup.minPurchaseSize || 0) &&
        selectedPartySize <= (ticketGroup.maxPurchaseSize || 0)
    );
    return filteredTicketGroups.reduce(accumulateAvailableTicketsCountByTime, {});
  }
);

export const ticketTypePurchaseSizeDateLockedTimes = defaultMemoize(
  (
    ticketGroups: consumer.CalendarTicketGroup[],
    ticketTypeId: number,
    purchaseSize: number,
    date: string
  ) => {
    return _.reduce(
      _.filter(ticketGroups, (ticketGroup) => {
        return (
          ticketGroupOfferedForTicketTypeAndPurchaseSize(ticketGroup, ticketTypeId, purchaseSize) &&
          ticketGroupFullyLockedForTicketTypeAndPurchaseSize(
            ticketGroup,
            ticketTypeId,
            purchaseSize
          ) &&
          ticketGroup.businessDay === date
        );
      }),
      accumulateByTime.bind(null, purchaseSize),
      {}
    );
  }
);

export const ticketTypePurchaseSizeDateSoldTimes = defaultMemoize(
  (
    ticketGroups: consumer.CalendarTicketGroup[],
    ticketTypeId: number,
    purchaseSize: number,
    date: string
  ) => {
    return _.reduce(
      _.filter(ticketGroups, (ticketGroup) => {
        return (
          ticketGroupOfferedForTicketTypeAndPurchaseSize(ticketGroup, ticketTypeId, purchaseSize) &&
          !ticketGroupAvailableForTicketTypeAndPurchaseSize(
            ticketGroup,
            ticketTypeId,
            purchaseSize
          ) &&
          ticketGroup.businessDay === date
        );
      }),
      accumulateByTime.bind(null, purchaseSize),
      {}
    );
  }
);

/**
 * Get ALL possible offered tickets and aggregate their data by time
 */
export const ticketTypeOfferedTimes = defaultMemoize(
  (ticketGroups: consumer.CalendarTicketGroup[], ticketTypeId: number) => {
    return _.reduce(
      _.filter(ticketGroups, (ticketGroup) => {
        return ticketGroupMatchesTicketTypeId(ticketGroup, ticketTypeId);
      }),
      accumulateByTime.bind(null, -1),
      {}
    );
  }
);

/**
 * Get offered tickets for a date and aggregate their data by time
 */
export const ticketTypeOfferedTimesForDate = defaultMemoize(
  (ticketGroups: consumer.CalendarTicketGroup[], ticketTypeId: number, date: string) => {
    return _.reduce(
      _.filter(ticketGroups, (ticketGroup) => {
        return (
          ticketGroupMatchesTicketTypeId(ticketGroup, ticketTypeId) &&
          ticketGroup.businessDay === date
        );
      }),
      accumulateByTime.bind(null, -1),
      {}
    );
  }
);

export const ticketGroupsByDate = defaultMemoize(
  (ticketGroups: consumer.ICalendarTicketGroup[]) => {
    return _.reduce(
      ticketGroups,
      (acc: Dictionary<consumer.ICalendarTicketGroup[]>, tg) => {
        const businessDay = tg.businessDay || '';
        if (!acc[businessDay]) {
          acc[businessDay] = [];
        }
        acc[businessDay].push(tg);
        return acc;
      },
      {}
    );
  }
);

export interface SearchTimeMap {
  [dateTime: string]: {
    ticketGroups: consumer.ICalendarTicketGroup[];
    isAvailable?: boolean;
  };
}
export interface SearchResponse {
  [ticketTypeId: number]: {
    ticketType: core.TicketType;
    offeredDates: DateMap;
    wasOfferedOnDay: boolean;
    wasOfferedAtSize: boolean;
    wasOfferedOnDayAndSize: boolean;
    availableTimes: Dictionary<Stats>;
    availableDates: DateMap;
    times: SearchTimeMap;
  };
}

export const searchCalendarByPartySize = defaultMemoize(
  (
    ticketGroups: consumer.ICalendarTicketGroup[],
    ticketTypes: core.TicketType[],
    partySize: number,
    date: string
  ): SearchResponse => {
    const response: SearchResponse = {};
    const ticketTypeMap = _.keyBy(ticketTypes, (ticketType) => ticketType.id);

    // Keep the ordering of ticketTypes.
    _.forEach(ticketTypes, (ticketType) => {
      response[ticketType.id] = {
        times: {},
        ticketType,
        offeredDates: {},
        wasOfferedOnDay: false,
        wasOfferedAtSize: false,
        wasOfferedOnDayAndSize: false,
        availableDates: {},
        availableTimes: {},
      };
    });

    const ticketGroupsAtDate = date ? ticketGroupsByDate(ticketGroups)[date] || [] : ticketGroups;
    _.forEach(ticketGroupsAtDate, (tg: consumer.ICalendarTicketGroup) => {
      const ticketTypeId = tg.ticketTypePrice?.[0].ticketTypeId;
      if (!ticketTypeId) {
        return;
      }
      if (!response[ticketTypeId]) {
        return;
      }

      const purchaseSize = getPurchaseSizeFromPartySize(
        partySize,
        ticketTypeMap[ticketTypeId]?.singleUnitQuantity
      );

      const dateTime = tg.date + 'T' + tg.time;
      if (
        !purchaseSize ||
        (purchaseSize >= (tg.minPurchaseSize || 0) && purchaseSize <= (tg.maxPurchaseSize || 0))
      ) {
        // We require the times past midnight to later in the ordering.
        if (!response[ticketTypeId].times[dateTime]) {
          response[ticketTypeId].times[dateTime] = {
            ticketGroups: [],
          };
        }
        response[ticketTypeId].times[dateTime].isAvailable =
          response[ticketTypeId].times[dateTime].isAvailable ||
          (_.has(
            [TicketGroupStatus.LOCKED, TicketGroupStatus.AVAILABLE],
            ticketGroupStatusForPurchaseSize(tg, purchaseSize)
          ) &&
            purchaseSize <= (tg.availableTickets || 0) &&
            // We want to check that isExclusivityActive is false directly given that
            // the variable can also be undefined or null. This would make sure that
            // the ticket group exclusivity its inactive and not other possible value.
            (tg.exclusivity === 'NONE' || tg.isExclusivityActive === false));
        response[ticketTypeId].times[dateTime].ticketGroups.push(tg);
      }
    });

    _.forEach(response, (perExperience, ticketTypeIdString) => {
      const ticketTypeId = _.toNumber(ticketTypeIdString);
      const ticketType = _.find(ticketTypes, (t) => t.id === ticketTypeId);
      if (!ticketType) {
        return;
      }

      const purchaseSize = getPurchaseSizeFromPartySize(partySize, ticketType.singleUnitQuantity);

      perExperience.ticketType = ticketType;
      perExperience.offeredDates = getTicketTypeOfferedDates(ticketGroups, ticketTypeId);
      perExperience.wasOfferedOnDay = perExperience.offeredDates[date];
      perExperience.wasOfferedAtSize = _.includes(ticketType.purchaseSize, purchaseSize);
      perExperience.wasOfferedOnDayAndSize = ticketTypePurchaseSizeOfferedDates(
        ticketGroups,
        ticketTypeId,
        purchaseSize
      )[date];
      perExperience.availableTimes = ticketTypePurchaseSizeDateAvailableTimes(
        ticketGroups,
        ticketTypeId,
        purchaseSize,
        date
      );
      perExperience.availableDates = ticketTypePurchaseSizeAvailableDates(
        ticketGroups,
        ticketTypeId,
        purchaseSize
      );
    });

    return response;
  }
);

export const getTicketTypeStatusForPurchaseSize = defaultMemoize(
  (
    ticketGroups: consumer.CalendarTicketGroup[],
    ticketType: core.TicketType,
    purchaseSize: number,
    date: string
  ) => {
    const sizes = ticketType.purchaseSize;
    const ticketTypeId = ticketType.id;
    const notOfferedForSize = !_.includes(sizes, purchaseSize);
    const notOfferedForSizeDate = !ticketTypePurchaseSizeOfferedDates(
      ticketGroups,
      ticketTypeId,
      purchaseSize
    )[date];
    const notOfferedForDate = !getTicketTypeOfferedDates(ticketGroups, ticketTypeId)[date];
    const soldOutForSize =
      ticketTypePurchaseSizeOfferedDates(ticketGroups, ticketTypeId, purchaseSize)[date] &&
      !ticketTypePurchaseSizeAvailableDates(ticketGroups, ticketTypeId, purchaseSize)[date];
    const isFullySoldOut = _.every(sizes, (s) =>
      _.isEmpty(ticketTypePurchaseSizeAvailableDates(ticketGroups, ticketTypeId, s))
    );

    return {
      notOfferedForSize,
      notOfferedForSizeDate,
      notOfferedForDate,
      soldOutForSize,
      isFullySoldOut,
    };
  }
);

// Gets the number of available and unavailable (sold + locked) tickets on the calendar
export const getAvailabilityStats = defaultMemoize((calendar: consumer.ConsumerFullCalendar) => {
  return _.reduce(
    _.filter(calendar.ticketGroup as consumer.CalendarTicketGroup[], isBookable),
    (prev, ticketGroup) => {
      return {
        available: prev.available + ticketGroup.availableTickets,
        unavailable: prev.unavailable + ticketGroup.soldTickets + ticketGroup.lockedTickets,
      };
    },
    {
      available: 0,
      unavailable: 0,
    }
  );
});

export const ticketTypeOpenDates = defaultMemoize(
  (ticketTypeCalendarDetails: consumer.TicketTypeCalendarDetails[], ticketTypeId: number) => {
    return _.reduce(
      oc(_.find(ticketTypeCalendarDetails, { ticketTypeId })).openDate([]),
      (acc: Dictionary<boolean>, date: string) => {
        acc[date] = true;
        return acc;
      },
      {}
    );
  }
);

export function ticketGroupExclusiveTimesByPartySize(
  ticketGroups: consumer.CalendarTicketGroup[],
  ticketTypeId: number,
  date: string,
  partySize: number
): ExclusivityMap {
  const singleUnitQuantity = getSingleUnitQuantityForTicketTypeFromGroups(
    ticketGroups,
    ticketTypeId
  );
  return ticketGroupExclusiveTimesByPurchaseSize(
    ticketGroups,
    ticketTypeId,
    date,
    getPurchaseSizeFromPartySize(partySize, singleUnitQuantity)
  );
}

export const ticketGroupExclusiveTimesByPurchaseSize = defaultMemoize(
  (
    ticketGroups: consumer.CalendarTicketGroup[],
    ticketTypeId: number,
    date: string,
    purchaseSize: number
  ): ExclusivityMap => {
    const availableTicketGroups = _.filter(ticketGroupsByDate(ticketGroups)[date], (ticketGroup) =>
      ticketGroupAvailableForTicketTypeAndPurchaseSize(ticketGroup, ticketTypeId, purchaseSize)
    );

    return _.reduce(
      _.concat(availableTicketGroups, availableTicketGroups),
      (exclusivityMap: ExclusivityMap, ticketGroup: consumer.ICalendarTicketGroup) => {
        const hasExclusivity = !safeEnumCompare(
          ticketGroup.exclusivity,
          Exclusivity,
          Exclusivity.NONE
        );
        _.update(
          exclusivityMap,
          ticketGroup.time || '',
          (previousValue: ExclusivityEntry[] | undefined) => {
            if (hasExclusivity) {
              return !previousValue ||
                previousValue.findIndex(
                  (exclusiveEntry) => exclusiveEntry.exclusivity === ticketGroup.exclusivity
                ) === -1
                ? [
                    ...(previousValue || []),
                    {
                      exclusivity: ticketGroup.exclusivity,
                      isExclusivityActive: ticketGroup.isExclusivityActive,
                      hasNonExclusive: false,
                    },
                  ]
                : previousValue;
            } else {
              return previousValue?.map((entry) => ({ ...entry, hasNonExclusive: true }));
            }
          }
        );
        return exclusivityMap;
      },
      {}
    );
  }
);

export function findFocusDate(
  event: React.KeyboardEvent<HTMLButtonElement>,
  date: string
): LocalDate | undefined {
  event.persist();
  const oldDate = LocalDate.parse(date);
  let newDate;
  switch (event.key) {
    case 'ArrowLeft':
      cancelEvent(event);
      newDate = findNewFocusDate(oldDate, -1);
      break;
    case 'ArrowRight':
      cancelEvent(event);
      newDate = findNewFocusDate(oldDate, 1);
      break;
    case 'ArrowUp':
      cancelEvent(event);
      newDate = findNewFocusDate(oldDate, -7);
      break;
    case 'ArrowDown':
      cancelEvent(event);
      newDate = findNewFocusDate(oldDate, 7);
      break;
    case 'Enter':
      cancelEvent(event);
      newDate = oldDate;
      break;
    default:
      break;
  }
  return newDate;
}

export function findNewFocusDate(date: LocalDate, difference: number): LocalDate {
  return date.plusDays(difference);
}

export function cancelEvent(event: React.SyntheticEvent) {
  event.preventDefault();
  event.stopPropagation();
}
